import '@/styles/globals.css';
import 'nprogress/nprogress.css';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { NextIntlClientProvider } from 'next-intl';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import NProgress from 'nprogress';
import { NextPage } from 'next';
import { Toaster } from '@/components/ui/toaster';
import ToolsAssessment from '@/components/commons/ToolsAssessment';
import Script from 'next/script';
import { usePathname } from 'next/navigation';
import EnglishClass from '@/components/commons/EnglishClass';

NProgress.configure({ showSpinner: false });

type PageProps = {
  messages: IntlMessages;
  now: number;
};

export type NextPageWithLayout<P = unknown> = NextPage<P> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout<P = unknown> = AppProps<P> & {
  Component: NextPageWithLayout<P>;
  pageProps: PageProps;
  userAgent?: string;
  head?: any;
  props?: any;
};

App.getInitialProps = async ({ ctx }: any) => {
  const userAgent = ctx?.req ? ctx.req?.headers?.['user-agent'] : navigator?.userAgent;
  return { pageProps: { userAgent } };
};

export default function App({ Component, pageProps }: AppPropsWithLayout<PageProps>) {
  const router = useRouter();
  const pathname = usePathname();
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);
  const [showEnglishClass, setShowEnglishClass] = useState(true);

  useEffect(() => {
    const handleStart = () => {
      NProgress.start();
    };

    const handleStop = () => {
      NProgress.done();
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleStop);
    router.events.on('routeChangeError', handleStop);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleStop);
      router.events.off('routeChangeError', handleStop);
    };
  }, [router]);

  return (
    <NextIntlClientProvider
      // To achieve consistent date, time and number formatting
      // across the app, you can define a set of global formats.
      formats={{
        dateTime: {
          short: {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          },
        },
      }}
      messages={pageProps.messages}
      // Providing an explicit value for `now` ensures consistent formatting of
      // relative values regardless of the server or client environment.
      now={new Date(pageProps.now)}
      // Also an explicit time zone is helpful to ensure dates render the
      // same way on the client as on the server, which might be located
      // in a different time zone.
      timeZone="Austria/Vienna"
      locale={router.locale ?? 'zh'}
    >
      {/* 埋点 */}
      <Script id="global-tracking-script" strategy="afterInteractive">
        {`
          var _czc = _czc || [];
          (function () {
                  var um = document.createElement("script");
                  um.src = "https://s4.cnzz.com/z.js?id=1281408383&async=1";
                  var s = document.getElementsByTagName("script")[0];
                  s.parentNode.insertBefore(um, s);
          })();
          console.log('全局埋点脚本已加载');
        `}
      </Script>
      {getLayout(<Component {...pageProps} />)}
      <Toaster />
      {!['/eb3-application-process', '/us-eb3', '/eb3-successful-cases'].includes(pathname) && (
        <ToolsAssessment className="md:hidden" />
      )}
      {showEnglishClass && <EnglishClass onClose={() => setShowEnglishClass(false)} />}
    </NextIntlClientProvider>
  );
}
